'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import QuickViewModal from './QuickViewModal';
import AddToWishList from '../wishlist/AddToWishList';
import { useAuth } from '@/context/AuthContext';
import useDeviceType from '@/hook/useDeviceType';

const ProductCard = ({
  product,
  style,
  iconStyle,
  textSize,
  quickViewWidth,
  quickViewHeight,
  isSwiper,
}) => {
  const {
    product_images,
    item_slug,
    item_name,
    _id,
    variation_group_id,
    inventory,
    country,
    brand,
    awards,
    parent_category,
    sub_category,
    child_category,
  } = product;

  const { isSignInOpen } = useAuth();
  const deviceType = useDeviceType();
  const router = useRouter();

  const images = useMemo(() => {
    if (!product_images) return [];
    return Object.values(product_images).filter(Boolean);
  }, [product_images]);

  const [bgImg, setBgImg] = useState(product_images?.main_img);
  const [isHover, setIsHover] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  useEffect(() => {
    if (isSignInOpen) {
      setIsClicked(false);
    }
  }, [isSignInOpen]);

  const award = awards || [];

  const handleMouseEnter = useCallback(() => {
    setBgImg(
      images[Math.floor(Math.random() * images.length + 1)] ||
      product_images?.main_img
    );
    setIsHover(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setBgImg(
      images[Math.floor(Math.random() * images.length + 1)] ||
      product_images?.main_img
    );
    setIsHover(false);
  }, []);

  const redirectLink = `/product/${item_slug}/buy/${_id}`;

  const handleRedirect = (url) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <div
        onClick={() => {
          if (deviceType !== 'laptop') handleRedirect(redirectLink);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`${style} relative overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat `}
      >
        <img
          loading='lazy'
          src={bgImg}
          alt={item_name}
          className='h-full w-full object-cover transition-all duration-300'
        />

        {/* Overlay on hover */}
        <div
          onClick={() => handleRedirect(redirectLink)}
          className={`${isHover ? 'block' : 'hidden'} absolute inset-0 cursor-pointer bg-black opacity-30 transition-opacity duration-300`}
        />

        {/* Wishlist */}
        <div className='absolute -right-1 top-0 cursor-pointer z-10'>
          <AddToWishList
            productCategory={parent_category}
            productPrice={inventory?.list_price}
            productName={item_name}
            productId={_id}
            style='p-1 bg-white rounded-full'
          />
        </div>

        {/* Award and Details */}
        <div className={`${isHover ? 'flex flex-col duration-300' : 'hidden'} absolute inset-x-2 bottom-2 z-10`}>
          {award.length > 0 && (
            <div className='awards-shape flex w-fit flex-row gap-2 rounded-tl-lg rounded-tr-lg bg-white px-2 py-1'>
              {award.slice(0, 3).map((award) => (
                <img
                  key={award._id}
                  src={award.image}
                  className={`${iconStyle} rounded-full`}
                  alt={award.award_name || 'Award'}
                />
              ))}
            </div>
          )}

          <div className='flex flex-col rounded-b-lg rounded-tr-lg bg-white px-2 py-1 shadow'>
            <div className='relative flex items-start justify-between'>
              <div className='flex flex-col gap-1'>
                <div className='flex items-center gap-2'>
                  <Link href={`/brand/${brand?.slug}`} target='_blank'>
                    {brand?.brand_name}
                  </Link>
                  <Link href={`/brand/${brand?.slug}`} target='_blank'>
                    <img
                      src={brand?.brand_logo_url}
                      className={`${iconStyle} rounded-full object-contain`}
                      alt={brand?.brand_name}
                    />
                  </Link>
                  <img
                    src={country?.image}
                    className={`${iconStyle} rounded-full`}
                    alt={`${country?.country_name} - Flag`}
                  />
                </div>
                <Link href={redirectLink} target='_blank' className={`${textSize} text-gray-900`}>
                  {item_name}
                </Link>
              </div>

              {/* Price */}
              <div className='hidden sm:flex flex-col items-end mt-1'>
                <p className={`${textSize} font-bold whitespace-nowrap`}>₹ {inventory?.list_price}</p>
                {inventory?.max_retail_price !== inventory?.list_price && (
                  <p className={`${textSize} text-gray-500 line-through whitespace-nowrap`}>
                    ₹ {inventory?.max_retail_price}
                  </p>
                )}
              </div>

              {/* Quick View Icon */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsClicked(true);
                }}
                className={`${isSwiper ? 'hidden' : ''} absolute -right-4 -top-4 z-20`}
              >
                <button className='rounded-full bg-white text-[#4282ff]'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={quickViewWidth}
                    height={quickViewHeight}
                    viewBox='0 0 33 33'
                    fill='currentColor' // uses tailwind text color
                  >
                    <path d='M24.6885 18.0329H18.2248V24.4966H14.993V18.0329H8.5293V14.8011H14.993V8.33741H18.2248V14.8011H24.6885Z' />
                  </svg>
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal as fixed layer */}
      {isClicked && (
        <div className='fixed inset-0 z-[9999]'>
          <QuickViewModal
            onClose={() => setIsClicked(false)}
            isVisible={isClicked}
            productId={_id}
            variationId={variation_group_id}
            availableForSale={inventory?.qty_available}
            parentName={parent_category?.category_name}
            subName={sub_category?.category_name}
            childName={child_category?.category_name}
          />
        </div>
      )}
    </>
  );
};

export default ProductCard;
